import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, Send, Trash2, RefreshCw, Bell, Mail, Pencil, History, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";
import FollowupAuditPanel from "@/components/admin/FollowupAuditPanel";
import CsvDeletePanel from "@/components/admin/CsvDeletePanel";
import ClaimLinkAnalytics from "@/components/admin/ClaimLinkAnalytics";
import LegacySubcategoryReport from "@/components/admin/LegacySubcategoryReport";
import { SUPPLIER_TYPES } from "@/components/SupplierFilterPanel";

const REQUIRED_COLS = "company_name, email, country, category, sub_category, website, description";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
const longDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const editTitle = (r) => {
  const hist = r.previous_claim_emails || [];
  if (!hist.length) return "Edit company name, email, website, country and category";
  const lines = hist.map((h) => `${h.email || "—"} · Email 1: ${shortDate(h.claim_link_sent_at)} · Email 2: ${shortDate(h.claim_followup_sent_at)} (changed ${shortDate(h.changed_at)})`);
  return `Previous email address${hist.length === 1 ? "" : "es"}:\n${lines.join("\n")}`;
};

function ImportPanel({ onDone }) {
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/admin/free-listings/template", { responseType: "text" });
    const blob = new Blob([data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "free-listings-template.csv";
    a.click();
  };

  const submit = async () => {
    if (!csvText.trim()) return toast.error("Paste CSV text or choose a file first");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-listings/import", { csv_text: csvText });
      setResult(data);
      toast.success(`Imported ${data.created} listing(s), ${data.suppliers_created} new supplier(s)`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="free-import-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-serif text-xl text-[#11211A]">Bulk import free listings</div>
          <p className="text-sm text-[#7D8C85] mt-1 max-w-xl">
            Upload a CSV to auto-create unclaimed free listings. Columns: <code className="text-xs bg-[#F0EFEA] px-1">{REQUIRED_COLS}</code>.
            Header names are flexible — e.g. "Company name", "Email address", "Website", "Sub-category" all work. Comma, semicolon or tab separated.
            No emails are sent on import — use "Send claim link" when you're ready.
          </p>
        </div>
        <button type="button" onClick={downloadTemplate} className="btn-secondary text-xs" data-testid="free-import-template-btn">
          <Download className="w-4 h-4" /> Download template
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="free-import-file" />
      </div>
      <textarea
        rows={8}
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        placeholder={`${REQUIRED_COLS.replace(/, /g, ",")}\n"Acme Linens","sales@acme.com","Australia","Linen, Textiles & Soft Furnishings","Bed Linen","https://acme.com","Commercial bed linen"`}
        className="w-full mt-3 px-3 py-2 text-xs font-mono border border-[#C4CFC9] rounded-sm"
        data-testid="free-import-textarea"
      />
      <div className="mt-3 flex items-center gap-3">
        <button type="button" onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="free-import-submit">
          <Upload className="w-4 h-4" /> {busy ? "Importing…" : "Import listings"}
        </button>
        {csvText && <button type="button" onClick={() => { setCsvText(""); setResult(null); if (fileRef.current) fileRef.current.value = ""; }} className="btn-ghost text-xs">Clear</button>}
      </div>
      {result && (
        <div className="mt-4 text-sm space-y-2" data-testid="free-import-result">
          <div className="text-[#264839] font-medium">Created {result.created} listing(s) · {result.suppliers_created} new supplier account(s) · {result.skipped.length} skipped · {result.errors.length} error(s)</div>
          {result.skipped.length > 0 && (
            <ul className="text-xs text-[#7D8C85] list-disc pl-5">
              {result.skipped.map((s, i) => <li key={i}>Row {s.row} — {s.company}: {s.reason}</li>)}
            </ul>
          )}
          {result.errors.length > 0 && (
            <ul className="text-xs text-[#B35C4A] list-disc pl-5" data-testid="free-import-errors">
              {result.errors.map((e, i) => <li key={i}>Row {e.row} — {e.company || "?"}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ClaimFunnel({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/free-listings/funnel").then(({ data }) => setData(data)).catch(() => {}); }, [refreshKey]);
  if (!data) return null;
  const max = Math.max(1, ...data.steps.map((s) => s.count));
  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="claim-funnel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-xl text-[#11211A]">Claim progress</div>
          <p className="text-xs text-[#7D8C85] mt-0.5">How admin-created free listings move from import to paid.</p>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <div className={`font-serif text-2xl whitespace-nowrap ${data.followup_due_now ? "text-[#B35C4A]" : "text-[#11211A]"}`} data-testid="funnel-followup-due">{data.followup_due_now ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">due for email 2 now</div>
          </div>
          <div>
            <div className="font-serif text-2xl text-[#264839] whitespace-nowrap" data-testid="funnel-first-claim-email">{longDate(data.first_claim_email_sent_at)}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">first claim email sent</div>
            {data.first_claim_email_company && <div className="text-[10px] text-[#9AA6A0] truncate max-w-[180px]" title={data.first_claim_email_company} data-testid="funnel-first-claim-email-company">{data.first_claim_email_company}</div>}
          </div>
          <div><div className="font-serif text-2xl text-[#11211A]" data-testid="funnel-claim-rate">{data.claim_rate}%</div><div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">claim rate</div></div>
          <div><div className="font-serif text-2xl text-[#B8860B]" data-testid="funnel-upgrade-rate">{data.upgrade_rate}%</div><div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">upgrade rate</div></div>
        </div>
      </div>
      <div className="space-y-2">
        {data.steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-step-${s.key}`}>
            <div className="w-36 text-xs text-[#4A5550] shrink-0">{s.label}</div>
            <div className="flex-1 h-6 bg-[#F0EFEA] relative">
              <div className={`h-full ${i === data.steps.length - 1 ? "bg-[#B8860B]" : "bg-[#264839]"}`} style={{ width: `${Math.max(2, (s.count / max) * 100)}%`, opacity: 0.55 + (i * 0.1) }} />
            </div>
            <div className="w-10 text-right font-serif text-lg text-[#11211A]" data-testid={`funnel-count-${s.key}`}>{s.count}</div>
          </div>
        ))}
      </div>
      {data.awaiting_link > 0 && (
        <div className="mt-4 text-xs text-[#B35C4A]" data-testid="funnel-awaiting">{data.awaiting_link} unclaimed supplier{data.awaiting_link === 1 ? "" : "s"} still haven't been sent a claim link — select them below and use "Send claim links".</div>
      )}
    </div>
  );
}

function CreateListingModal({ row, onClose, onCreated }) {
  const [options, setOptions] = useState({ categories: [] });
  const [categoryIds, setCategoryIds] = useState([]);
  const [tags, setTags] = useState([]);
  const [types, setTypes] = useState(row.supplier_types || []);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/free-listings/options").then(({ data }) => setOptions(data)).catch(() => {}); }, []);
  const parents = options.categories.filter((c) => !c.parent_id);
  const subs = options.categories.filter((c) => c.parent_id && categoryIds.includes(c.parent_id));
  const tagOptions = parents.filter((p) => categoryIds.includes(p.id)).flatMap((p) => (p.subs || []).map((s) => ({ ...s, parent: p.name })));
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const submit = async (e) => {
    e.preventDefault();
    if (!categoryIds.length) { toast.error("Pick at least one category"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/free-listings/${row.id}/create-listing`, { category_ids: categoryIds, tags, description, supplier_types: types });
      toast.success(`Listing created for ${row.company} — ${data.claimed ? "Claimed" : "Unclaimed"} · ${data.status} · ${data.categories.join(", ")}`);
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create listing"); }
    finally { setSaving(false); }
  };
  const field = "w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm focus:outline-none focus:border-[#264839] mt-1";
  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/50 flex items-center justify-center p-4" data-testid="free-create-listing-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]" data-testid="free-create-form">
        <div className="px-6 pt-6 pb-4 border-b border-[#E8ECE9] flex-shrink-0">
          <div className="font-serif text-xl text-[#11211A]">Create listing for {row.company}</div>
          <p className="text-xs text-[#7D8C85] mt-1">{row.email}{row.website ? ` · ${row.website}` : ""} · account is <strong>{row.is_unclaimed ? "Unclaimed" : "Claimed"}</strong>. The listing is linked to this account and published as active.</p>
        </div>
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0" data-testid="free-create-scroll-body">
        <div>
          <div className="text-xs font-medium text-[#4A5550]">Categories</div>
          <div className="mt-1 grid sm:grid-cols-2 gap-1.5">
            {parents.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-[#11211A]">
                <input type="checkbox" checked={categoryIds.includes(p.id)} onChange={() => toggle(categoryIds, setCategoryIds, p.id)} className="accent-[#264839]" data-testid={`create-listing-cat-${p.id}`} /> {p.name}
              </label>
            ))}
          </div>
          {subs.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {subs.map((s) => (
                <label key={s.id} className={`text-xs px-2 py-1 border cursor-pointer ${categoryIds.includes(s.id) ? "bg-[#264839] text-white border-[#264839]" : "border-[#C4CFC9] text-[#4A5550]"}`}>
                  <input type="checkbox" className="hidden" checked={categoryIds.includes(s.id)} onChange={() => toggle(categoryIds, setCategoryIds, s.id)} /> {s.name}
                </label>
              ))}
            </div>
          )}
        </div>
        {tagOptions.length > 0 && (
          <div>
            <div className="text-xs font-medium text-[#4A5550]">Product tags (optional)</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tagOptions.map((t) => (
                <label key={t.tag} className={`text-xs px-2 py-1 border cursor-pointer ${tags.includes(t.tag) ? "bg-[#264839] text-white border-[#264839]" : "border-[#C4CFC9] text-[#4A5550]"}`} title={t.parent}>
                  <input type="checkbox" className="hidden" checked={tags.includes(t.tag)} onChange={() => toggle(tags, setTags, t.tag)} /> {t.label}
                </label>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="text-xs font-medium text-[#4A5550]">Supplier type</div>
          <div className="mt-1 flex flex-wrap gap-3">
            {SUPPLIER_TYPES.map((t) => (
              <label key={t} className="flex items-center gap-1.5 text-sm text-[#4A5550]"><input type="checkbox" checked={types.includes(t)} onChange={() => toggle(types, setTypes, t)} className="accent-[#264839]" data-testid={`create-listing-type-${t.toLowerCase()}`} /> {t}</label>
            ))}
          </div>
        </div>
        <label className="block text-xs font-medium text-[#4A5550]">Description (optional)
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={field} placeholder={`${row.company} — what they supply to hotels`} data-testid="create-listing-description" />
        </label>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E8ECE9] bg-white flex-shrink-0" data-testid="free-create-footer">
          <button type="button" onClick={onClose} className="btn-ghost text-xs" data-testid="create-listing-cancel">Cancel</button>
          <button type="submit" disabled={saving || !categoryIds.length} className="btn-primary text-xs disabled:opacity-50" data-testid="create-listing-submit">{saving ? "Creating…" : "Create active listing"}</button>
        </div>
      </form>
    </div>
  );
}

function EditSupplierModal({ row, onClose, onSaved, onSendClaim }) {
  const [form, setForm] = useState({ company: row.company || "", email: row.email || "", website: row.website || "", country_code: row.country_code || "", supplier_types: row.supplier_types || [], logo_url: row.logo_url || "", custom_oem: !!row.custom_oem });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml|gif)$/.test(file.type)) { toast.error("Please choose a PNG, JPG, WEBP, SVG or GIF image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded — click Save to apply it to the listing");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingLogo(false); e.target.value = ""; }
  };
  const [cats, setCats] = useState(row.listings.map((l) => ({ id: l.id, category_ids: l.category_ids?.length ? l.category_ids : [l.category_id].filter(Boolean), tags: l.tags || (l.tag ? [l.tag] : []), description: l.description || "", legacy_tags: l.legacy_tags || [] })));
  const [initial] = useState(() => JSON.stringify({ form, cats }));
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirty = JSON.stringify({ form, cats }) !== initial;
  const requestClose = () => { if (dirty) setConfirmDiscard(true); else onClose(); };
  const [options, setOptions] = useState({ countries: [], categories: [] });
  const [saving, setSaving] = useState(false);
  const [savedEmail, setSavedEmail] = useState(null);
  useEffect(() => { api.get("/admin/free-listings/options").then(({ data }) => setOptions(data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCat = (id, patch) => setCats((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/free-listings/${row.id}`, { ...form, listings: cats });
      toast.success(`Saved — ${data.listings_updated} listing${data.listings_updated === 1 ? "" : "s"} updated`);
      if (data.email_changed) setSavedEmail(data.email);
      else onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSaving(true);
    try { await onSendClaim({ ...row, email: savedEmail }); onSaved(); }
    finally { setSaving(false); }
  };
  const [resending, setResending] = useState(false);
  const resendClaim = async () => {
    if (dirty && !window.confirm("You have unsaved edits. Send the claim link to the email currently on file anyway?")) return;
    setResending(true);
    try { await onSendClaim(row); } finally { setResending(false); }
  };
  const field = "w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm focus:outline-none focus:border-[#264839] normal-case tracking-normal mt-1";
  if (savedEmail) {
    return (
      <div className="fixed inset-0 z-50 bg-[#11211A]/50 flex items-center justify-center p-4" data-testid="free-edit-modal">
        <div className="bg-white w-full max-w-md p-6 space-y-4 shadow-xl" data-testid="free-edit-send-prompt">
          <div>
            <div className="font-serif text-xl text-[#11211A]">Email updated</div>
            <p className="text-sm text-[#4A5550] mt-2">Send the claim link for <span className="font-medium text-[#11211A]">{form.company}</span> to the new address <span className="font-medium text-[#11211A]" data-testid="free-edit-new-email">{savedEmail}</span> now?</p>
            <p className="text-xs text-[#7D8C85] mt-1">This becomes Email 1 for the new address; the 7-day follow-up (Email 2) then runs automatically.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onSaved} className="btn-ghost text-xs" data-testid="free-edit-send-later">Not now</button>
            <button type="button" onClick={sendNow} disabled={saving} className="btn-primary text-xs" data-testid="free-edit-send-now"><Send className="w-3 h-3 inline -mt-0.5" /> {saving ? "Sending…" : "Send claim link now"}</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/50 flex items-center justify-center p-4" onClick={requestClose} data-testid="free-edit-modal">
      {confirmDiscard && (
        <div className="fixed inset-0 z-[60] bg-[#11211A]/40 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setConfirmDiscard(false); }} data-testid="free-edit-discard-prompt">
          <div className="bg-white w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="font-serif text-xl text-[#11211A]">Discard unsaved changes?</div>
            <p className="text-sm text-[#4A5550] mt-2">You have edits that haven't been saved. Close anyway and lose them?</p>
            <div className="flex justify-end gap-2 pt-5">
              <button type="button" onClick={() => setConfirmDiscard(false)} className="btn-ghost text-xs" data-testid="free-edit-discard-keep">Keep editing</button>
              <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.15em] px-4 py-2 bg-[#B35C4A] text-white hover:bg-[#994A3B] transition-colors" data-testid="free-edit-discard-confirm">Discard changes</button>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md shadow-xl flex flex-col max-h-[90vh]" data-testid="free-edit-form">
        <div className="px-6 pt-6 pb-4 border-b border-[#E8ECE9] flex-shrink-0">
          <div className="font-serif text-xl text-[#11211A]">Edit supplier</div>
          <p className="text-xs text-[#7D8C85] mt-0.5">Changes apply to the account and all {row.listing_count} listing{row.listing_count === 1 ? "" : "s"} immediately.</p>
        </div>
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0" data-testid="free-edit-scroll-body">
        <label className="block text-xs uppercase tracking-wider text-[#7D8C85]">Company name
          <input required value={form.company} onChange={set("company")} className={field} data-testid="free-edit-company" />
        </label>
        <label className="block text-xs uppercase tracking-wider text-[#7D8C85]">Email address
          <input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="free-edit-email" />
        </label>
        <label className="block text-xs uppercase tracking-wider text-[#7D8C85]">Website URL
          <input value={form.website} onChange={set("website")} placeholder="www.example.com" className={field} data-testid="free-edit-website" />
        </label>
        <div data-testid="free-edit-logo-block">
          <div className="text-xs uppercase tracking-wider text-[#7D8C85]">Logo / image</div>
          <div className="mt-1 flex items-center gap-3">
            <div className="w-16 h-16 border border-[#E8ECE9] bg-[#F7F6F2] flex items-center justify-center overflow-hidden shrink-0" data-testid="free-edit-logo-preview">
              {form.logo_url ? <img src={form.logo_url.startsWith("http") ? form.logo_url : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="Supplier logo" className="w-full h-full object-contain" /> : <span className="text-[10px] text-[#7D8C85]">No logo</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-2 text-xs border border-[#C4CFC9] px-3 py-1.5 cursor-pointer hover:border-[#264839] bg-white">
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" onChange={uploadLogo} className="hidden" data-testid="free-edit-logo-input" />
                {uploadingLogo ? "Uploading…" : form.logo_url ? "Replace logo" : "Upload logo"}
              </label>
              {form.logo_url && <button type="button" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} className="text-[11px] text-[#B35C4A] hover:underline text-left" data-testid="free-edit-logo-remove">Remove logo</button>}
              <div className="text-[10px] text-[#7D8C85]">PNG, JPG, WEBP or SVG · shown on their listing page and card</div>
            </div>
          </div>
        </div>
        <label className="block text-xs uppercase tracking-wider text-[#7D8C85]">Country
          <select value={form.country_code} onChange={set("country_code")} className={field} data-testid="free-edit-country">
            <option value="">— Not set —</option>
            {options.countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <div className="text-xs uppercase tracking-wider text-[#7D8C85]">Supplier type
          <div className="grid grid-cols-2 gap-y-1.5 mt-1.5 normal-case tracking-normal" data-testid="free-edit-supplier-types">
            {SUPPLIER_TYPES.map((t) => {
              const on = form.supplier_types.includes(t);
              return (
                <label key={t} className="inline-flex items-center gap-1.5 text-sm text-[#4A5550] cursor-pointer">
                  <input type="checkbox" checked={on} onChange={() => setForm((f) => ({ ...f, supplier_types: on ? f.supplier_types.filter((x) => x !== t) : [...f.supplier_types, t] }))} className="accent-[#264839]" data-testid={`free-edit-type-${t.toLowerCase()}`} />
                  {t}
                </label>
              );
            })}
          </div>
          <span className="block normal-case tracking-normal text-[10px] text-[#9AA6A0] mt-1">Tick every type that applies — the public Supplier type filter matches any of them.</span>
        </div>
        <label className="flex items-start gap-2.5 p-3 border border-[#E8ECE9] bg-[#FAFAF7] cursor-pointer" data-testid="free-edit-custom-oem-label">
          <input type="checkbox" checked={!!form.custom_oem} onChange={(e) => setForm((f) => ({ ...f, custom_oem: e.target.checked }))} className="accent-[#264839] mt-0.5" data-testid="free-edit-custom-oem" />
          <span>
            <span className="block text-sm font-medium text-[#11211A]">Custom / OEM available</span>
            <span className="block text-[11px] text-[#7D8C85]">Shows a “Custom / OEM” label on the supplier's card and includes them in the buyer Custom / OEM filter.</span>
          </span>
        </label>
        {cats.map((c, i) => {
          const parents = options.categories.filter((o) => !o.parent_id);
          const childrenOf = (pid) => options.categories.filter((o) => o.parent_id === pid && o.group).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          const tickedParents = parents.filter((o) => c.category_ids.includes(o.id));
          const toggleCat = (id) => {
            const on = c.category_ids.includes(id);
            const dropIds = on ? [id, ...childrenOf(id).map((k) => k.id)] : [];
            const nextIds = on ? c.category_ids.filter((x) => !dropIds.includes(x)) : [...c.category_ids, id];
            // keep tags still offered by a ticked parent OR not a current tag at all (legacy search tags) — never silently drop data
            const stillOffered = new Set(parents.filter((o) => nextIds.includes(o.id)).flatMap((o) => (o.subs || []).map((sub) => sub.tag)));
            const anyOffered = new Set(parents.flatMap((o) => (o.subs || []).map((sub) => sub.tag)));
            setCat(c.id, { category_ids: nextIds, tags: c.tags.filter((t) => stillOffered.has(t) || !anyOffered.has(t)) });
          };
          const toggleChild = (id) => setCat(c.id, { category_ids: c.category_ids.includes(id) ? c.category_ids.filter((x) => x !== id) : [...c.category_ids, id] });
          const toggleTag = (tag) => setCat(c.id, { tags: c.tags.includes(tag) ? c.tags.filter((t) => t !== tag) : [...c.tags, tag] });
          const legacy = (c.legacy_tags || []).filter((lt) => c.tags.includes(lt.tag));
          const applySuggestion = (lt) => {
            const parentOfChild = options.categories.find((o) => o.id === lt.suggested_child_id)?.parent_id;
            const ids = [...c.category_ids];
            if (parentOfChild && !ids.includes(parentOfChild)) ids.push(parentOfChild);
            if (!ids.includes(lt.suggested_child_id)) ids.push(lt.suggested_child_id);
            setCat(c.id, { category_ids: ids, tags: c.tags.filter((t) => t !== lt.tag) });
          };
          return (
            <div key={c.id} className="space-y-3" data-testid={`free-edit-listing-${c.id}`}>
              <div className="text-xs uppercase tracking-wider text-[#7D8C85]">{cats.length > 1 ? `Listing ${i + 1} categories` : "Categories"}
                <div className="grid grid-cols-2 gap-y-1.5 mt-1.5 normal-case tracking-normal max-h-40 overflow-y-auto pr-1" data-testid={`free-edit-category-${c.id}`}>
                  {parents.map((o) => (
                    <label key={o.id} className="inline-flex items-center gap-1.5 text-sm text-[#4A5550] cursor-pointer">
                      <input type="checkbox" checked={c.category_ids.includes(o.id)} onChange={() => toggleCat(o.id)} className="accent-[#264839]" data-testid={`free-edit-cat-${c.id}-${o.id}`} />
                      {o.name}
                    </label>
                  ))}
                </div>
                {c.category_ids.length === 0 && <span className="block normal-case tracking-normal text-[10px] text-[#B35C4A] mt-1">Tick at least one category.</span>}
                <span className="block normal-case tracking-normal text-[10px] text-[#9AA6A0] mt-1">The supplier appears under every ticked category on the public site.</span>
              </div>

              {legacy.length > 0 && (
                <div className="border border-[#E3B23C]/60 bg-[#FFF8E6] p-3 normal-case tracking-normal" data-testid={`free-edit-legacy-${c.id}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8A6508]">Needs updating · old sub-category values</div>
                  <p className="text-[11px] text-[#6B5A2E] mt-0.5">These came from the old category list. Use the suggested current sub-category, or keep them as plain search tags — nothing is deleted unless you remove it.</p>
                  <div className="mt-2 space-y-1.5">
                    {legacy.map((lt) => (
                      <div key={lt.tag} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`free-edit-legacy-tag-${c.id}-${lt.tag}`}>
                        <code className="px-1.5 py-0.5 bg-white border border-[#E3B23C]/50 text-[#4A5550]">{lt.tag}</code>
                        {lt.suggested_child_name ? (
                          <button type="button" onClick={() => applySuggestion(lt)} className="text-[#264839] underline underline-offset-2 hover:text-[#1B3329]" data-testid={`free-edit-legacy-use-${c.id}-${lt.tag}`}>→ Move to {lt.suggested_child_name}</button>
                        ) : <span className="text-[#8A6508]">no close match — tick a sub-category below</span>}
                        <span className="text-[#9AA6A0]">·</span>
                        <button type="button" onClick={() => toggleTag(lt.tag)} className="text-[#B35C4A] hover:underline" data-testid={`free-edit-legacy-remove-${c.id}-${lt.tag}`}>Remove</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tickedParents.some((p) => childrenOf(p.id).length > 0) && (
                <div className="text-xs uppercase tracking-wider text-[#7D8C85]">Sub-categories
                  <div className="mt-1.5 space-y-3 normal-case tracking-normal max-h-60 overflow-y-auto pr-1" data-testid={`free-edit-subcategory-${c.id}`}>
                    {tickedParents.filter((p) => childrenOf(p.id).length > 0).map((p) => {
                      const groups = childrenOf(p.id).reduce((acc, k) => { (acc[k.group] ||= []).push(k); return acc; }, {});
                      return Object.entries(groups).map(([g, kids]) => (
                        <div key={p.id + g}>
                          <div className="text-[10px] uppercase tracking-wider text-[#9AA6A0] mb-1">{g}</div>
                          <div className="grid grid-cols-2 gap-y-1">
                            {kids.map((k) => (
                              <label key={k.id} className="inline-flex items-center gap-1.5 text-sm text-[#4A5550] cursor-pointer">
                                <input type="checkbox" checked={c.category_ids.includes(k.id)} onChange={() => toggleChild(k.id)} className="accent-[#264839]" data-testid={`free-edit-sub-${c.id}-${k.id}`} />
                                {k.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      ));
                    })}
                  </div>
                </div>
              )}

              {tickedParents.some((o) => o.subs?.length > 0) && (
                <div className="text-xs uppercase tracking-wider text-[#7D8C85]">Product tags <span className="text-[#9AA6A0] normal-case tracking-normal">(optional — help search)</span>
                  <div className="mt-1.5 space-y-2 normal-case tracking-normal max-h-40 overflow-y-auto pr-1" data-testid={`free-edit-tags-${c.id}`}>
                    {tickedParents.filter((o) => o.subs?.length).map((o) => (
                      <div key={o.id} className="flex flex-wrap gap-1">
                        {o.subs.map((sub) => (
                          <label key={sub.tag} className={`text-[11px] px-2 py-0.5 border cursor-pointer ${c.tags.includes(sub.tag) ? "bg-[#264839] text-white border-[#264839]" : "border-[#C4CFC9] text-[#4A5550]"}`}>
                            <input type="checkbox" className="hidden" checked={c.tags.includes(sub.tag)} onChange={() => toggleTag(sub.tag)} data-testid={`free-edit-tag-${c.id}-${sub.tag}`} /> {sub.label}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <label className="block text-xs uppercase tracking-wider text-[#7D8C85]">Business description
                <textarea value={c.description || ""} onChange={(e) => setCat(c.id, { description: e.target.value })} rows={4} maxLength={2000} className={field} placeholder="What the supplier offers, who they serve, what makes them worth a quote…" data-testid={`free-edit-description-${c.id}`} />
                <span className="block normal-case tracking-normal text-[10px] text-[#9AA6A0] mt-1">{(c.description || "").length}/2000 · shown on the public listing after Save.</span>
              </label>
            </div>
          );
        })}
        </div>
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-[#E8ECE9] bg-white flex-shrink-0" data-testid="free-edit-footer">
          <div>
            {row.is_unclaimed && !row.claim_opted_out && (
              <button type="button" onClick={resendClaim} disabled={resending || saving} title={`Send a fresh magic link to ${row.email}`} className="text-xs border border-[#264839] text-[#264839] px-3 py-2 hover:bg-[#264839] hover:text-white transition disabled:opacity-50 inline-flex items-center gap-1" data-testid="free-edit-resend-claim">
                {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {resending ? "Sending…" : row.claim_link_sent_at ? "Resend claim link" : "Send claim link"}
              </button>
            )}
          </div>
          <div className="flex gap-2">
          <button type="button" onClick={requestClose} className="btn-ghost text-xs" data-testid="free-edit-cancel">Cancel</button>
          <button type="submit" disabled={saving || cats.some((c) => c.category_ids.length === 0)} className="btn-primary text-xs disabled:opacity-50" data-testid="free-edit-save">{saving ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function FreeListingsManager() {
  const [editing, setEditing] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [csvDeleteOpen, setCsvDeleteOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [claimed, setClaimed] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (claimed) params.set("claimed", claimed);
    setLoadError(null);
    try {
      const { data } = await api.get(`/admin/free-listings?${params.toString()}`);
      setRows(data.items);
    } catch (e) {
      setRows([]);
      setLoadError(describeLoadError(e, "free-tier suppliers"));
    }
    setSelected(new Set());
    setRefreshKey((k) => k + 1);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, claimed]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const unclaimedRows = rows;
  const toggleAll = () => setSelected(selected.size === unclaimedRows.length ? new Set() : new Set(unclaimedRows.map((r) => r.id)));

  const sendOne = async (r) => {
    try {
      await api.post(`/admin/free-listings/${r.id}/send-claim-link`);
      toast.success(`Claim link sent to ${r.email}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
  };

  const sendBulk = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Send claim links to ${selected.size} free-tier supplier(s)? Anyone on a paid plan is skipped automatically.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-listings/bulk-send-claim-links", { user_ids: [...selected] });
      toast.success(`Sent ${data.sent} claim link${data.sent === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped${data.paid_skipped ? ` (${data.paid_skipped} on a paid plan)` : ""}` : ""}`);
      (data.skipped_items || []).filter((s) => !/paid plan/.test(s.reason || "")).slice(0, 3).forEach((s) => toast.warning(`${s.email || s.id}: ${s.reason}`));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk send failed"); }
    finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.company || r.email} and their ${r.listing_count} listing(s)? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${r.id}`, { data: { confirm: "DELETE" } });
      toast.success("Supplier deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const removeBulk = async () => {
    if (!selected.size) return;
    const ids = [...selected];
    const listings = rows.filter((r) => selected.has(r.id)).reduce((n, r) => n + (r.listing_count || 0), 0);
    const typed = window.prompt(`You are about to permanently delete ${ids.length} supplier${ids.length === 1 ? "" : "s"} and their ${listings} listing(s). This cannot be undone.\n\nType DELETE to confirm:`);
    if ((typed || "").trim() !== "DELETE") { if (typed !== null) toast.error("Cancelled — you must type DELETE exactly"); return; }
    setBusy(true);
    let deleted = 0; const skipped = [];
    try {
      for (let i = 0; i < ids.length; i += 200) {
        const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids.slice(i, i + 200), confirm: "DELETE" });
        deleted += data.deleted_count; skipped.push(...(data.skipped || []));
      }
      (skipped.length ? toast.warning : toast.success)(`Deleted ${deleted} supplier${deleted === 1 ? "" : "s"}${skipped.length ? ` · ${skipped.length} skipped` : ""}`, { duration: 8000 });
      skipped.slice(0, 3).forEach((s) => toast.warning(`${s.email || s.id}: ${s.reason}`));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk delete failed"); }
    finally { setBusy(false); }
  };

  const toggleOptOut = async (r) => {
    const next = !r.claim_opted_out;
    if (next && !window.confirm(`Stop all claim emails to ${r.company || r.email}?`)) return;
    try {
      await api.post(`/admin/free-listings/${r.id}/opt-out`, { opted_out: next });
      toast.success(next ? "Opted out — no further claim emails" : "Claim emails re-enabled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const downloadAll = async () => {
    try {
      const { data } = await api.get("/admin/free-listings/export", { responseType: "text" });
      const blob = new Blob([data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `free-suppliers-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success("Supplier CSV downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
  };

  const runReminderSweep = async () => {
    if (!window.confirm("Send the 7-day claim follow-up (email 2) now to every unclaimed supplier whose claim link is at least 7 days old and who hasn't been reminded yet?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-listings/nudge-claim-reminders");
      const s = data.stats;
      const msg = `Email 2 sent to ${s.sent} supplier${s.sent === 1 ? "" : "s"} — ${s.scanned} checked · ${s.too_recent || 0} too recent (<7 days) · ${s.already_sent || 0} already followed up · ${s.failed || 0} rejected by email provider`;
      (s.failed ? toast.warning : toast.success)(msg, { duration: 12000 });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Sweep failed"); }
    finally { setBusy(false); }
  };

  const runUpgradeNudges = async () => {
    if (!window.confirm("Send the month-end upgrade nudge NOW to every claimed free supplier (with their view count)? This normally runs automatically on the last day of the month.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-listings/send-upgrade-nudges?force=true");
      toast.success(`Upgrade nudges: ${data.stats.sent} sent · ${data.stats.skipped} skipped (no listing) · ${data.stats.failed} failed`);
    } catch (err) { toast.error(err.response?.data?.detail || "Nudge failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="free-listings-manager">
      {editing && <EditSupplierModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onSendClaim={sendOne} />}
      {creatingFor && <CreateListingModal row={creatingFor} onClose={() => setCreatingFor(null)} onCreated={() => { setCreatingFor(null); load(); }} />}
      <ClaimFunnel refreshKey={refreshKey} />
      <LegacySubcategoryReport refreshKey={refreshKey} onApplied={load} />
      <ClaimLinkAnalytics refreshKey={refreshKey} />
      <ImportPanel onDone={load} />

      <div className="bg-white border border-[#E8ECE9]">
        <div className="p-4 border-b border-[#E8ECE9] flex flex-wrap items-center gap-3">
          <div className="font-serif text-lg text-[#11211A] flex-1">Free-tier suppliers <span className="text-sm text-[#7D8C85]">({rows.length})</span></div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company / email" className="px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="free-search" />
          <select value={claimed} onChange={(e) => setClaimed(e.target.value)} className="px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="free-filter-claimed">
            <option value="">All</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="claimed">Claimed</option>
            <option value="registered">Registered · no listing</option>
          </select>
          <button type="button" onClick={downloadAll} className="btn-secondary text-xs" title="CSV of every free-tier supplier: company, email, website, category, subcategory, claimed status" data-testid="free-download-all-btn">
            <Download className="w-3.5 h-3.5" /> Download all suppliers
          </button>
          <button type="button" onClick={() => setCsvDeleteOpen((v) => !v)} className={`text-xs border border-[#B35C4A] text-[#B35C4A] px-3 py-1.5 inline-flex items-center gap-1 hover:bg-[#B35C4A] hover:text-white transition ${csvDeleteOpen ? "ring-2 ring-[#B35C4A]" : ""}`} title="Upload a CSV; rows with Y in column D are deleted after you confirm the count" data-testid="free-csv-delete-btn">
            <Trash2 className="w-3.5 h-3.5" /> Delete from CSV
          </button>
          <button type="button" onClick={runReminderSweep} disabled={busy} className="btn-secondary text-xs disabled:opacity-50" title="Runs automatically every 24h — use this to send due reminders right now" data-testid="free-run-reminder-sweep">
            <Bell className="w-3.5 h-3.5" /> Run 7-day follow-ups
          </button>
          <button type="button" onClick={() => setAuditOpen((v) => !v)} className={`btn-secondary text-xs ${auditOpen ? "ring-2 ring-[#264839]" : ""}`} title="Who received Email 2 on a given day and how old their claim link was" data-testid="free-followup-audit-btn">
            <History className="w-3.5 h-3.5" /> Audit follow-ups
          </button>
          <button type="button" onClick={runUpgradeNudges} disabled={busy} className="btn-secondary text-xs disabled:opacity-50" title="Sends automatically on the last day of each month — use this to send now" data-testid="free-run-upgrade-nudges">
            <Mail className="w-3.5 h-3.5" /> Send upgrade nudges
          </button>
          <button type="button" onClick={load} className="btn-ghost text-xs" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {auditOpen && <div className="px-4 pb-4"><FollowupAuditPanel onClose={() => setAuditOpen(false)} /></div>}
        {csvDeleteOpen && <div className="px-4 pb-4"><CsvDeletePanel onClose={() => setCsvDeleteOpen(false)} onDeleted={load} /></div>}
        {selected.size > 0 && (
          <div className="px-4 py-2 bg-[#F0EFEA] flex flex-wrap items-center gap-3 text-sm" data-testid="free-bulk-bar">
            <span data-testid="free-bulk-count">{selected.size} of {rows.length} selected</span>
            {selected.size < rows.length && (
              <button type="button" onClick={() => setSelected(new Set(rows.map((r) => r.id)))} className="text-xs underline text-[#264839]" data-testid="free-select-all-matching">
                Select all {rows.length} matching{q || claimed ? " this filter" : ""}
              </button>
            )}
            <button type="button" onClick={sendBulk} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="free-bulk-send-claim">
              <Send className="w-3.5 h-3.5" /> Send claim links
            </button>
            <button type="button" onClick={removeBulk} disabled={busy} className="text-xs border border-[#B35C4A] text-[#B35C4A] px-3 py-1.5 inline-flex items-center gap-1 hover:bg-[#B35C4A] hover:text-white transition disabled:opacity-50" data-testid="free-bulk-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete selected
            </button>
            <button type="button" onClick={() => setSelected(new Set())} className="btn-ghost text-xs" data-testid="free-bulk-clear">Clear</button>
          </div>
        )}
        <div className="overflow-x-auto">
          {loadError && <div className="p-4"><LoadErrorBanner error={loadError} onRetry={load} testId="free-listings-error" /></div>}
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-[#7D8C85] bg-[#FAFAF7]">
              <tr>
                <th className="p-3"><input type="checkbox" title={`Select all ${rows.length} shown`} checked={unclaimedRows.length > 0 && selected.size === unclaimedRows.length} onChange={toggleAll} data-testid="free-select-all" /></th>
                <th className="p-3">Company</th>
                <th className="p-3">Email</th>
                <th className="p-3">Website</th>
                <th className="p-3 whitespace-nowrap">Email 1</th>
                <th className="p-3 whitespace-nowrap">Email 2</th>
                <th className="p-3">Country</th>
                <th className="p-3">Type</th>
                <th className="p-3">Categories</th>
                <th className="p-3">Status</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loadError && (
                <tr><td colSpan={10} className="p-8 text-center text-[#7D8C85]" data-testid="free-empty">No free-tier suppliers yet — import a CSV above.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#E8ECE9]" data-testid={`free-row-${r.id}`}>
                  <td className="p-3"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`free-select-${r.id}`} /></td>
                  <td className="p-3">
                    <div className="text-[#11211A] font-medium">{r.company || "—"}</div>
                    {r.listing_ids[0] && <a href={`/listing/${r.listing_ids[0]}`} target="_blank" rel="noreferrer" className="block text-[11px] text-[#264839] hover:underline">View listing ↗</a>}
                  </td>
                  <td className="p-3 text-[#4A5550]">
                    <div>{r.email}</div>
                    {r.email_bounced_at && (
                      <span title={`${r.email_bounce_type || "Bounce"} · ${shortDate(r.email_bounced_at)}: ${r.email_bounce_reason || "Email could not be delivered"}`} className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider bg-[#B35C4A] text-white px-2 py-0.5 cursor-help" data-testid={`free-bounced-${r.id}`}>
                        <AlertTriangle className="w-3 h-3" /> Bounced
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs" data-testid={`free-website-${r.id}`}>
                    {r.website ? <a href={r.website} target="_blank" rel="noreferrer" className="text-[#264839] hover:underline">{r.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a> : <span className="text-[#9AA6A0]">—</span>}
                  </td>
                  <td className="p-3 text-xs text-[#4A5550] whitespace-nowrap" data-testid={`free-email1-${r.id}`}>{shortDate(r.claim_link_sent_at)}</td>
                  <td className={`p-3 text-xs whitespace-nowrap ${r.claim_followup_sent_at ? "text-[#B35C4A]" : "text-[#4A5550]"}`} data-testid={`free-email2-${r.id}`}>{shortDate(r.claim_followup_sent_at)}</td>
                  <td className="p-3 text-[#4A5550]">{r.country || "—"}</td>
                  <td className="p-3 text-[#4A5550]" data-testid={`free-type-${r.id}`}>{(r.supplier_types || []).length ? r.supplier_types.join(", ") : <span className="text-[#B8860B]" title="Not set — this supplier is invisible to the Supplier type filter">—</span>}</td>
                  <td className="p-3 text-[#4A5550] text-xs">
                    {r.categories.join(", ") || <span className="text-[#B35C4A]">No listing</span>}
                    {r.removed_listing_count > 0 && <div className="text-[10px] text-[#9AA6A0] mt-0.5" title="Soft-deleted by the supplier — restorable for 30 days in Deletion Audit" data-testid={`free-removed-${r.id}`}>{r.removed_listing_count} removed (restorable)</div>}
                    {r.needs_update && <div className="inline-block text-[9px] uppercase tracking-wider font-semibold text-[#8A6508] bg-[#FFF4DC] px-1.5 py-0.5 mt-1" title="Has sub-category values from the old category list — open Edit to map them" data-testid={`free-needs-update-${r.id}`}>Needs updating</div>}
                  </td>
                  <td className="p-3">
                    {r.claim_state === "unclaimed" || (r.claim_state === undefined && r.is_unclaimed)
                      ? <span className="text-[10px] uppercase tracking-wider bg-[#FBECE8] text-[#B35C4A] px-2 py-0.5" data-testid={`free-status-${r.id}`}>Unclaimed</span>
                      : r.claim_state === "registered"
                        ? <span className="text-[10px] uppercase tracking-wider bg-[#FFF4DC] text-[#8A6508] px-2 py-0.5" title="Signed up on the site but never created a listing — nothing was claimed or removed" data-testid={`free-status-${r.id}`}>Registered · no listing</span>
                        : <span className="text-[10px] uppercase tracking-wider bg-[#E8ECE9] text-[#264839] px-2 py-0.5" data-testid={`free-status-${r.id}`}>Claimed</span>}
                    {r.listing_source === "csv_import" && <div className="text-[10px] text-[#9AA6A0] mt-1">CSV import</div>}
                    {r.claim_opted_out && <div className="text-[10px] uppercase tracking-wider text-[#B35C4A] font-semibold mt-1" data-testid={`free-opted-out-${r.id}`}>Opted out</div>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {r.listings.length === 0 && (
                        <button type="button" onClick={() => setCreatingFor(r)} title="This account has no directory listing — create one linked to it" className="text-xs border border-[#B8860B] text-[#8A6508] px-2 py-1 hover:bg-[#B8860B] hover:text-white transition" data-testid={`free-create-listing-${r.id}`}>
                          + Create listing
                        </button>
                      )}
                      <button type="button" onClick={() => setEditing(r)} title={editTitle(r)} className={`text-xs border px-2 py-1 transition ${r.previous_claim_emails?.length ? "border-[#B8860B] text-[#8A6508] hover:bg-[#FBF5E6]" : "border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] hover:text-[#264839]"}`} data-testid={`free-edit-${r.id}`}>
                        <Pencil className="w-3 h-3 inline -mt-0.5" /> Edit{r.previous_claim_emails?.length ? <History className="w-3 h-3 inline -mt-0.5 ml-1" /> : null}
                      </button>
                      <button type="button" onClick={() => toggleOptOut(r)} title={r.claim_opted_out ? "Allow claim emails again" : "Supplier replied 'remove' — stop all claim emails"} className={`text-xs px-2 py-1 border transition ${r.claim_opted_out ? "border-[#B35C4A] bg-[#B35C4A] text-white" : "border-[#C4CFC9] text-[#7D8C85] hover:border-[#B35C4A] hover:text-[#B35C4A]"}`} data-testid={`free-opt-out-${r.id}`}>
                        {r.claim_opted_out ? "Opted out" : "Opt out"}
                      </button>
                      <button type="button" onClick={() => sendOne(r)} className="text-xs border border-[#264839] text-[#264839] px-2 py-1 hover:bg-[#264839] hover:text-white transition" data-testid={`free-send-claim-${r.id}`}>
                        <Send className="w-3 h-3 inline -mt-0.5" /> {r.claim_link_sent_at ? "Resend" : "Send claim link"}
                      </button>
                      <button type="button" onClick={() => remove(r)} className="text-xs border border-[#B35C4A] text-[#B35C4A] px-2 py-1 hover:bg-[#B35C4A] hover:text-white transition" data-testid={`free-delete-${r.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
